import Button from '@/components/Buttons/Button';
import { toast } from '@/components/toast';
import { featchOrg, updatePresenterOwner } from '@/services/api/did-api/backoffice-api';
import { useAuth } from '@/services/auth';
import { Box, FormControl, InputLabel, MenuItem, Paper, Select, styled, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

interface PresenterOwnerFormProps {
    onSuccess?: () => void;
    onError?: (error: string) => void;
}

const StyledButton = styled(Button)(({ theme }) => ({
    padding: theme.spacing(1, 2),
    fontSize: '1.5rem',
}));

export const PresenterOwnerForm = ({ onSuccess, onError }: PresenterOwnerFormProps) => {
    const { user } = useAuth();
    const [presenterId, setPresenterId] = useState('');
    const [currentOwnerId, setCurrentOwnerId] = useState('');
    const [newOwnerId, setNewOwnerId] = useState('');
    const [useMe, setUseMe] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [orgName, setOrgName] = useState<string | null>(null);
    const [orgNotFound, setOrgNotFound] = useState(false);

    const isV2Presenter = presenterId.startsWith('v2_');
    const isFormValid = isV2Presenter && presenterId && newOwnerId && (useMe || currentOwnerId) && !orgNotFound;

    useEffect(() => {
        const fetchOrgName = async () => {
            if (newOwnerId.startsWith('org_')) {
                try {
                    const org = await featchOrg(newOwnerId);
                    setOrgName(org.name);
                    setOrgNotFound(false);
                } catch (error) {
                    setOrgName(null);
                    setOrgNotFound(true);
                }
            } else {
                setOrgName(null);
                setOrgNotFound(false);
            }
        };

        fetchOrgName();
    }, [newOwnerId]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const payload = {
                ownerId: useMe ? user?.id : currentOwnerId,
                newOwnerId: newOwnerId,
            };

            await updatePresenterOwner(presenterId, payload);
            toast.success({ title: 'Presenter owner updated successfully' });
            onSuccess?.();

            setPresenterId('');
            setCurrentOwnerId('');
            setNewOwnerId('');
            setUseMe(true);
        } catch (error: any) {
            const errorMessage = error?.response?.data?.message || 'Failed to update presenter owner';
            toast.error({ title: errorMessage });
            onError?.(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
                Modify Presenter Owner
            </Typography>
            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                    label="Presenter ID"
                    value={presenterId}
                    onChange={e => setPresenterId(e.target.value)}
                    required
                    fullWidth
                    error={presenterId !== '' && !isV2Presenter}
                    helperText={presenterId !== '' && !isV2Presenter ? 'Only v2 presenters are supported' : ''}
                />

                <FormControl fullWidth>
                    <InputLabel>Current Owner</InputLabel>
                    <Select
                        value={useMe ? 'me' : 'other'}
                        onChange={e => setUseMe(e.target.value === 'me')}
                        label="Current Owner"
                        defaultValue="me">
                        <MenuItem value="me">Me</MenuItem>
                        <MenuItem value="other">Other User</MenuItem>
                    </Select>
                </FormControl>

                {!useMe && (
                    <TextField
                        label="Current Owner ID"
                        value={currentOwnerId}
                        onChange={e => setCurrentOwnerId(e.target.value)}
                        required
                        fullWidth
                    />
                )}

                <TextField
                    label="New Owner ID"
                    value={newOwnerId}
                    onChange={e => setNewOwnerId(e.target.value)}
                    required
                    fullWidth
                    error={orgNotFound}
                    helperText={orgNotFound ? 'Organization not found' : ''}
                />

                {orgName && (
                    <Typography variant="h5" sx={{ mt: 1, mb: 1, color: 'primary.main' }}>
                        Organization: {orgName}
                    </Typography>
                )}

                <StyledButton type="submit" variant="contained" disabled={isSubmitting || !isFormValid}>
                    {isSubmitting
                        ? 'Updating...'
                        : orgNotFound
                          ? 'Organization Not Found'
                          : orgName
                            ? `Change Owner to ${orgName}`
                            : 'Change Owner'}
                </StyledButton>
            </Box>
        </Paper>
    );
};
